// docs/121 VW1: the village's scenery the same in every view. Nothing here is random per client: who
// passes along the street and when, where the hens peck, where the inn's cat sleeps and the dog lies,
// the guest strolling the gallery and which guest says what, are all functions of the village's (or the
// visit's) seed and the World's clock (`smoothServerNow`, docs/78), the way the villagers' ambles are
// (docs/120 VM2 `ambleOf`). A view that comes in late sees each of them where the others do.
import { hashN, type Station } from './layout';
import { findPath, lengthOf, nearestFree, pointAt, type Pt, type WalkGrid } from './walk';
import { CRITTER_PACE, type CritterKind } from './critters';

/** One of a list, by a 0–1 hash. */
export const pickBy = <T,>(xs: readonly T[], h: number): T => xs[Math.min(xs.length - 1, Math.floor(h * xs.length))]!;
/** The list in an order every view agrees on (a hash shuffle). */
export function shuffleBy<T>(xs: readonly T[], seed: number, salt: number): T[] {
  return xs.map((x, i) => ({ x, k: hashN(seed, salt, i) })).sort((a, b) => a.k - b.k).map((o) => o.x);
}

/** The `k`th round of something that happens every `period` seconds, and how far into it `t` is. */
export const roundOf = (t: number, period: number): { k: number; into: number } => { const k = Math.floor(t / period); return { k, into: t - k * period }; };

// ---- the street's passers-by -------------------------------------------------------------------------
/** Townsfolk frames for passers-by (creature sheet, frame A; A + 18 the off beat). */
export const FOLK = [38, 39, 40, 41, 42, 43, 44, 49, 50] as const;
/** A passer-by sets off once a round, somewhen in its first part. */
export const PASSER_ROUND = 8;
/** No walk across the street takes longer than this (s), so only rounds this recent can be on it. */
const PASSER_LONGEST = 120;

export interface Passer {
  k: number;
  kind: 'folk' | CritterKind;
  frame: number;
  coat: number;
  /** Their walk (cells), when they set off (World s) and how fast. */
  line: Pt[];
  start: number;
  pace: number;
  /** Into a doorway at the end (fades out), out of one at the start. */
  intoDoor: boolean;
  fromDoor: boolean;
  /** A bark or a meow as they go. */
  call: boolean;
}

export interface StreetShape { w: number; doors: readonly number[]; night: boolean; grid: WalkGrid }

/** The `k`th round's passer-by (none: a quiet spell; fewer after dark). */
export function passerOf(seed: number, k: number, s: StreetShape): Passer | undefined {
  const h = (i: number) => hashN(seed, k, 0x9a55, i);
  if (h(0) < (s.night ? 0.45 : 0.12)) return undefined;
  const roll = h(1);
  const kind: Passer['kind'] = s.night ? (roll < 0.5 ? 'cat' : 'folk') : roll < 0.62 ? 'folk' : roll < 0.84 ? 'dog' : 'cat';
  const y = kind === 'folk' ? 5.4 + h(2) * 1.1 : 6.1 + h(2) * 0.5;
  const fromDoor = kind === 'folk' && s.doors.length > 0 && h(3) < 0.3 ? pickBy(s.doors, h(4)) : undefined;
  const intoDoor = kind === 'folk' && fromDoor === undefined && s.doors.length > 0 && h(5) < 0.3 ? pickBy(s.doors, h(6)) : undefined;
  const left = h(7) < 0.5;
  const a: Pt = fromDoor !== undefined ? { x: fromDoor, y: 5.1 } : { x: left ? -1.4 : s.w + 0.4, y };
  const b: Pt = intoDoor !== undefined ? { x: intoDoor, y: 5.1 } : { x: fromDoor !== undefined ? (h(8) < 0.5 ? -1.4 : s.w + 0.4) : left ? s.w + 0.4 : -1.4, y };
  return {
    k, kind, frame: pickBy(FOLK, h(11)), coat: Math.floor(h(12) * 4),
    line: findPath(s.grid, a, b), start: k * PASSER_ROUND + h(10) * PASSER_ROUND * 0.7,
    pace: kind === 'folk' ? 0.7 + h(9) * 0.3 : CRITTER_PACE[kind],
    intoDoor: intoDoor !== undefined, fromDoor: fromDoor !== undefined,
    call: kind === 'dog' ? h(13) < 0.5 : kind === 'cat' ? h(13) < 0.4 : false,
  };
}

/** The rounds whose passers-by may be on the street at `t` (s). */
export function passerRounds(t: number): number[] {
  const last = Math.floor(t / PASSER_ROUND), first = Math.floor((t - PASSER_LONGEST) / PASSER_ROUND);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/** Where a passer-by is at `t`: how far along their walk (cells), or none (not set off, or gone). */
export function passerAlong(p: Passer, t: number): number | undefined {
  const s = (t - p.start) * p.pace;
  return s < 0 || s >= lengthOf(p.line) ? undefined : s;
}

// ---- a wander about a home: villagers' ambles, the hens' pecking, the gallery guest -----------------
export interface Wander {
  /** Seconds a step lasts: `base` give or take `spread` (seeded per walker). */
  period: [number, number];
  /** How far either side of home (cells) a step may go, and the rows it keeps to (`y0` + up to `dy`). */
  dx: number; y0: number; dy: number;
  pace: number;
}

/** A number for a walker's key (a stable hash of the string). */
export const keyNum = (k: string) => { let h = 7; for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0; return h; };

/** Which step of their round a walker is on at `t`, and how far into it. Cheap: asked every frame. */
export function wanderStep(seed: number, key: string, t: number, o: Pick<Wander, 'period'>): { n: number; into: number } {
  const h = keyNum(key);
  const period = o.period[0] + hashN(seed, h, 1) * o.period[1];
  const u = t / period + hashN(seed, h, 2);
  const n = Math.floor(u);
  return { n, into: (u - n) * period };
}

/**
 * A walker's `n`th step about `home` at World time `t` (s): from where the last one ended to where this
 * one goes, `into` seconds ago, at `speed`, facing `face` at the end. The same seed, walker and time
 * give the same step in every view.
 */
export function wanderOf(seed: number, key: string, home: Pt, g: WalkGrid, t: number, o: Wander): { n: number; from: Pt; to: Pt; into: number; speed: number; face: 1 | -1 } {
  const { n, into } = wanderStep(seed, key, t, o);
  const h = keyNum(key);
  const at = (i: number): Pt => nearestFree(g, { x: home.x + (hashN(seed, h, i, 3) * 2 - 1) * o.dx, y: o.y0 + hashN(seed, h, i, 4) * o.dy });
  return { n, from: at(n - 1), to: at(n), into, speed: o.pace * (0.8 + hashN(seed, h, n, 5) * 0.4), face: hashN(seed, h, n, 6) < 0.5 ? -1 : 1 };
}

/** docs/120 VM2: a villager's amble up and down the lane. */
export const AMBLE: Omit<Wander, 'pace'> = { period: [8, 9], dx: 2.5, y0: 5.1, dy: 0.9 };
/** docs/121 VW1: a hen pecking about the verge. */
export const PECK: Wander = { period: [1.5, 4], dx: 1.5, y0: 6.1, dy: 0.5, pace: CRITTER_PACE.hen };

// ---- the inn's cat and dog (docs/116 CI13) -------------------------------------------------------------
/** The cat finds somewhere else to sleep once a round; the dog gets up and trots about once a round. */
export const CAT_ROUND = 95, DOG_ROUND = 43;
export const CAT_PACE = 0.9, DOG_PACE = 1.4;

export type CatSpot = Station & { sill?: boolean };

/** Which of her spots the cat sleeps on in round `k` (she may stay where she is). */
export const catSpotOf = (seed: number, k: number, n: number): number => Math.floor(hashN(seed, k, 0xca7) * n) % Math.max(1, n);

/** Where the dog goes in round `k`: home to his place by the door, or off to a spot on the floor. */
export function dogGoal(seed: number, k: number, home: Station, g: WalkGrid, roomW: number): { to: Pt; home: boolean; bark: boolean } {
  const h = (i: number) => hashN(seed, k, 0xd06, i);
  if (h(0) < 0.4) return { to: home, home: true, bark: h(3) < 0.3 };
  return { to: nearestFree(g, { x: 2 + h(1) * (roomW - 5), y: g.y0 + 1 + h(2) * Math.max(0.5, g.y1 - g.y0 - 1) }), home: false, bark: h(3) < 0.3 };
}

/** A walk from `a` to `b` (cells) at `pace`, `into` seconds after it set off: where the walker is and the rest of it (none: arrived). */
export function walkAt(g: WalkGrid, a: Pt, b: Pt, pace: number, into: number): { at: Pt; line: Pt[]; along: number; done: boolean } {
  const line = findPath(g, a, b);
  const len = lengthOf(line), along = Math.max(0, into) * pace;
  if (along >= len) return { at: line[line.length - 1] ?? b, line, along: len, done: true };
  const p = pointAt(line, along);
  return { at: { x: p.x, y: p.y }, line, along, done: false };
}

// ---- docs/121 VW2: a game of fetch, from the moment's seed -------------------------------------------
export interface Fetch {
  /** Where the stick lands, and how long it flies (ms). */
  land: Pt;
  flyMs: number;
  /** Which way the thrower faces. */
  dir: 1 | -1;
  /** What the thrower says when the dog brings it back (none: nothing). */
  praise?: string;
}
const PRAISE = ['Good dog!', 'Again?', 'Who\'s a good boy?', 'Well fetched!'];
/** The throw, the same in every view: from the thrower's place, into the room, by the seed. */
export function fetchOf(seed: number, from: Pt, g: WalkGrid, roomW: number): Fetch {
  const h = (i: number) => hashN(seed, 0xf37c, i);
  const dir = from.x < roomW / 2 ? 1 : -1;
  const land = nearestFree(g, { x: Math.max(1.5, Math.min(roomW - 3, from.x + dir * (3.5 + h(1) * 3))), y: Math.max(g.y0 + 0.5, Math.min(g.y1, from.y + (h(2) - 0.3) * 1.2)) });
  return { land, flyMs: 520 + Math.abs(land.x - from.x) * 70, dir, ...(h(3) < 0.5 ? { praise: pickBy(PRAISE, h(4)) } : {}) };
}

// ---- docs/121 VW3: a band's meal at the inn, as the other views see it ---------------------------------
/** What diners say over their meal once it is eaten. */
export const TABLE_TALK = ['Ha!', '…and then the goblin says…', 'Another round!', '♪', 'Mm!', 'Cheers!', '*hic*', 'Best stew in the realm.', 'Hah, true!', '…'];
/** A line round the table every round (s), shown for its first `TALK_SHOWN` s. */
export const TALK_ROUND = 3.4, TALK_SHOWN = 1.7;

/** Who at the table says what at `t` (World s), from the meal's seed (when it was ordered): the same in every view. */
export function tableTalkAt(seed: number, diners: readonly string[], t: number): { key: string; text: string; n: number; laugh: boolean } | undefined {
  const { k, into } = roundOf(t, TALK_ROUND);
  if (into > TALK_SHOWN || !diners.length || hashN(seed, k, 1) < 0.25) return undefined;
  return { key: pickBy(diners, hashN(seed, k, 2)), text: pickBy(TABLE_TALK, hashN(seed, k, 3)), n: k, laugh: hashN(seed, k, 4) < 0.3 };
}

/** How a meal seen from another view is going `secs` after it was ordered: the plates full, half, clean; eating. */
export const MEAL_SERVED_S = 9, MEAL_HALF_S = 12, MEAL_EATEN_S = 15;
/** A meal is shown on another band's table for as long as they sit there, up to this long (s). */
export const MEAL_SHOWN_S = 1800;
export function mealAt(secs: number): { served: boolean; left: 0 | 1 | 2; eating: boolean; resting: boolean } {
  return { served: secs >= MEAL_SERVED_S, left: secs < MEAL_HALF_S ? 0 : secs < MEAL_EATEN_S ? 1 : 2, eating: secs >= MEAL_SERVED_S && secs < MEAL_EATEN_S, resting: secs >= MEAL_EATEN_S };
}

/** docs/121 VW10: the serving hand who brings another band's meal: out of the kitchen door so it is beside
 *  the table just before the plates are down, waits while they are set, then goes back in. Cells a second. */
export const SERVE_PACE = 2.2, SERVE_BY_S = MEAL_SERVED_S - 0.6, SERVE_BACK_S = MEAL_SERVED_S + 1.2;
/** Where that serving hand is `secs` after the meal was ordered (undefined while in the kitchen). */
export function serveAt(g: WalkGrid, door: Pt, by: Pt, secs: number): { leg: 'out' | 'back'; at: Pt; line: Pt[]; along: number; done: boolean; tray: boolean } | undefined {
  const outS = SERVE_BY_S - lengthOf(findPath(g, door, by)) / SERVE_PACE;
  if (secs < outS) return undefined;
  if (secs < SERVE_BACK_S) return { leg: 'out', ...walkAt(g, door, by, SERVE_PACE, secs - outS), tray: secs < MEAL_SERVED_S };
  const w = walkAt(g, by, door, SERVE_PACE, secs - SERVE_BACK_S);
  return w.done ? undefined : { leg: 'back', ...w, tray: false };
}
